import { clueHeading, formatDistanceAway, MAX_GUESSES, milesAway } from "@/daily/game";
import { dt } from "@/daily/strings";
import { DESKTOP_QUERY, useMediaQuery, useProgress } from "@/daily/hooks";
import { DailyClue, DailyGuess } from "@/daily/types";
import { FitText } from "./FitText";
import { ClueIcon } from "./icons";

interface GuessCounterProps {
  // Guesses whose result has been shown.
  guesses: DailyGuess[];
  // A guess is being checked: its dot pulses until the verdict lands.
  pending: boolean;
}

// "Guess 3 of 5" with one dot per guess.
export const GuessCounter = ({ guesses, pending }: GuessCounterProps) => (
  <div className="ds-counter">
    <span className="ds-counter__label">
      {dt("guessCount", { number: Math.min(guesses.length + 1, MAX_GUESSES), total: MAX_GUESSES })}
    </span>
    <span
      className="ds-counter__dots"
      aria-label={dt("guessesUsed", { used: guesses.length, total: MAX_GUESSES })}
    >
      {Array.from({ length: MAX_GUESSES }, (_, index) => {
        const guess = guesses[index];
        const state = guess
          ? guess.band
          : index === guesses.length
            ? pending
              ? "pending"
              : "current"
            : "unused";
        // Keyed by state so a dot replays its flip when the verdict lands.
        return (
          <span
            key={`${index}-${state}`}
            className={`ds-dot ds-dot--${state}`}
          />
        );
      })}
    </span>
  </div>
);

interface DistancePillProps {
  guess: DailyGuess;
  // The pill as a large banner in the middle of the map.
  large?: boolean;
  // Shown again from the guess's pin, after the clue is already out.
  review?: boolean;
  onSkip: () => void;
}

// "NOT ROME / 650 miles away": tells the player the guess was wrong and how
// far off it was. Tap to skip ahead to the clue.
export const DistancePill = ({
  guess,
  large,
  review,
  onSkip,
}: DistancePillProps) => {
  // The distance climbs from zero: the longer it runs, the further off it was.
  const progress = useProgress(guess.airport.iata, 900);
  const finalDistance = formatDistanceAway(guess.distanceMiles);
  const distance =
    progress >= 1
      ? finalDistance
      : milesAway(Math.round((guess.distanceMiles * progress) / 10) * 10);

  return (
    <button
      type="button"
      className={`ds-pill ds-pill--distance ${large ? "ds-pill--large" : ""}`}
      onClick={onSkip}
      aria-label={`${dt("notCity", { city: guess.airport.city })}. ${finalDistance}. ${review ? dt("tapClose") : dt("tapClue")}`}
    >
      <span className={`ds-pill__code ds-pill__code--${guess.band}`}>
        {guess.airport.iata}
      </span>
      <span className="ds-pill__text">
        <span className="ds-pill__eyebrow">{dt("notCity", { city: guess.airport.city })}</span>
        <span
          className="ds-pill__main ds-pill__count"
          // How many characters the line runs to, so a phone can size it to fit.
          style={
            {
              "--ds-chars": finalDistance.length,
            } as React.CSSProperties
          }
        >
          {/* Invisible copies of the widest text the count can reach, so the
              pill is its final width from the first frame and never resizes. */}
          {[
            finalDistance,
            finalDistance.replace(/\d/g, "0"),
            finalDistance.replace(/\d/g, "8"),
          ].map((sizer, index) => (
            <span
              key={index}
              className="ds-pill__count-sizer"
              aria-hidden="true"
            >
              {sizer}
            </span>
          ))}
          <span>{distance}</span>
        </span>
      </span>
    </button>
  );
};

interface CluePillProps {
  clue: DailyClue;
  number: number;
  isNew?: boolean;
  large?: boolean;
  // Shows the full sentence under the short form.
  withDetail?: boolean;
  onOpen: () => void;
}

// A revealed clue: icon, "CLUE 2 · ON ARRIVAL" and the short fact. Used for
// the reveal after a wrong guess, and as the clue stack on desktop.
export const CluePill = ({
  clue,
  number,
  isNew,
  large,
  withDetail,
  onOpen,
}: CluePillProps) => {
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  // The banner is a step smaller on a phone so it fits across the screen.
  const factSize = large ? (isDesktop ? 38 : 26) : 20;

  return (
    <button
      type="button"
      className={`ds-pill ds-pill--clue ${isNew ? "ds-glow" : ""} ${large ? "ds-pill--large" : ""} ${withDetail ? "ds-pill--detailed" : ""}`}
      onClick={onOpen}
      aria-label={`${clueHeading(number, clue.type)}: ${clue.shortFact}. ${dt("tapDetails")}`}
    >
      <span className="ds-pill__icon">
        <ClueIcon type={clue.type} size={large ? (isDesktop ? 36 : 28) : 22} />
      </span>
      <span className="ds-pill__text">
        <span className="ds-pill__eyebrow">
          {clueHeading(number, clue.type)}
        </span>
        <FitText className="ds-pill__main" max={factSize}>
          {clue.shortFact}
        </FitText>
        {withDetail && <span className="ds-clue-detail">{clue.detail}</span>}
      </span>
    </button>
  );
};
